import { type ChangeEvent } from "react";
import { useCanvasStore } from "@/store/useCanvasStore";
import { NODE_COLOR_PALETTE } from "@/config/theme";
import { CV } from "@/config/canvasSizing";

// Right-side tool-options panel for the pencil. Mirrors PropertiesPanel styling.
// Only mounted while the pencil tool is active (eraser has no options).
export default function PencilOptions() {
  const activeTool        = useCanvasStore((s) => s.activeTool);
  const pencilColor       = useCanvasStore((s) => s.pencilColor);
  const pencilWidth       = useCanvasStore((s) => s.pencilWidth);
  const pencilHighlighter = useCanvasStore((s) => s.pencilHighlighter);
  const setPencilColor       = useCanvasStore((s) => s.setPencilColor);
  const setPencilWidth       = useCanvasStore((s) => s.setPencilWidth);
  const setPencilHighlighter = useCanvasStore((s) => s.setPencilHighlighter);
  const pencilOptionsOpen    = useCanvasStore((s) => s.pencilOptionsOpen);

  if (activeTool !== "pencil") return null;

  return (
    <div
      data-testid="pencil-options"
      className={`absolute right-3 top-16 w-52 bg-canvas-node border border-canvas-node-border rounded-xl shadow-xl shadow-black/40 p-3 flex flex-col gap-3 z-40 transition-opacity duration-200 ${
        pencilOptionsOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <p className="text-xs font-semibold text-canvas-text">Pencil</p>

      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wider text-canvas-muted font-mono">
          Thickness ({pencilWidth})
        </span>
        <input
          data-testid="pencil-width"
          type="range"
          min={CV.drawing.minWidth}
          max={CV.drawing.maxWidth}
          step={1}
          value={pencilWidth}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPencilWidth(Number(e.target.value))}
          className="w-full accent-canvas-accent"
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wider text-canvas-muted font-mono">Color</span>
        <div className="flex flex-wrap gap-1.5">
          {NODE_COLOR_PALETTE.map((c) => (
            <button
              key={c.name}
              type="button"
              data-testid={`pencil-color-${c.name}`}
              aria-label={c.name}
              onClick={() => setPencilColor(c.name)}
              className={`w-4 h-4 rounded-full ${c.swatchClass} ${
                pencilColor === c.name ? "ring-2 ring-offset-1 ring-canvas-accent" : ""
              }`}
            />
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs text-canvas-text">
        <input
          data-testid="pencil-highlighter"
          type="checkbox"
          checked={pencilHighlighter}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPencilHighlighter(e.target.checked)}
        />
        Highlighter
      </label>
    </div>
  );
}
